// No router/SDK imports: this small accumulator runs before hydration. It observes
// navigation without patching Next's private router or issuing additional RUM events.
type Operation = 'router_push' | 'router_replace' | 'router_traverse' | 'history_push' | 'history_replace';
const QUERY_KEYS = ['live_chat', 'op_market_ticker', 'op_order_side', 'op_side', 'op_order_type', 'sortMarkets'];
const MAX_OPERATIONS = 16;
const LOOKBACK_MS = 5_000;

type Navigation = {
    operation: Operation;
    at: number;
    url_change: 'none' | 'query' | 'hash' | 'path' | 'origin' | 'unknown';
    changed_query_keys?: string[];
    other_query_keys_changed?: boolean;
};
let scope: { pathname: string; operations: Navigation[]; dropped: number } | undefined;

export function startEventPageNavigationTrace(pathname: string): void {
    scope = { pathname, operations: [], dropped: 0 };
}

export function stopEventPageNavigationTrace(): void {
    scope = undefined;
}

/** Router starts and successful History writes are distinct observations, not causal claims. */
export function recordEventPageNavigation(
    operation: Operation,
    targetUrl: string | URL | null | undefined,
    previousUrl?: string,
): void {
    // Fail open: telemetry must never interrupt a router call or a successful history write.
    try {
        if (!scope || scope.pathname !== location.pathname || document.visibilityState !== 'visible') return;
        const entry: Navigation = { operation, at: performance.now(), url_change: 'unknown' };
        // popstate has already changed location when Next announces a traverse. There is
        // no reliable previous query here; do not report a misleading empty diff.
        if (operation !== 'router_traverse') {
            const from = previousUrl ?? location.href;
            const before = new URL(from);
            const after = new URL(targetUrl ?? from, from);
            entry.url_change =
                before.origin !== after.origin
                    ? 'origin'
                    : before.pathname !== after.pathname
                      ? 'path'
                      : before.search !== after.search
                        ? 'query'
                        : before.hash !== after.hash
                          ? 'hash'
                          : 'none';
            const changed = new Set([...before.searchParams.keys(), ...after.searchParams.keys()]);
            for (const key of changed) {
                if (JSON.stringify(before.searchParams.getAll(key)) === JSON.stringify(after.searchParams.getAll(key)))
                    changed.delete(key);
            }
            // Even arbitrary parameter NAMES may contain user data. Only these fixed
            // application keys leave this function; values/URLs/history state never do.
            entry.changed_query_keys = QUERY_KEYS.filter((key) => changed.has(key));
            entry.other_query_keys_changed = changed.size > entry.changed_query_keys.length;
        }
        if (scope.operations.length === MAX_OPERATIONS) {
            scope.operations.shift();
            scope.dropped++;
        }
        scope.operations.push(entry);
    } catch {
        // Invalid URLs or unavailable browser APIs must not affect navigation.
    }
}

export function getEventPageNavigationTrace(flashStart: number, flashEnd: number) {
    if (!scope || scope.pathname !== location.pathname) return undefined;
    return {
        schema_version: 1,
        lookback_ms: LOOKBACK_MS,
        dropped_operations: scope.dropped,
        operations: scope.operations
            .filter(({ at }) => at >= flashStart - LOOKBACK_MS && at <= flashEnd)
            .map(({ at, ...entry }) => ({
                ...entry,
                changed_query_keys: entry.changed_query_keys?.slice(),
                offset_ms: Math.round(at - flashStart),
            })),
    };
}
